import { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { useField } from 'formik';
import getCaretCoordinates from 'textarea-caret';

import isEmpty from 'Lib/utilities/isEmpty';

import Generative from '../common/Generative';

import HelperText from './HelperText';

export default function TextAreaField({ generative, minRows = 4, name, trim, onBlur, margin, maxLength, required, helperText, predicted = undefined, ...rest }) {
	const ref = useRef();

	const [field, meta, helper] = useField(name);

	const [position, setPosition] = useState({});

	useEffect(() => {
		const inputElement = ref.current;

		if (generative) {
			if (inputElement) {
				inputElement.addEventListener('selectionchange', getSelectionCoordinates);
			}
		}

		return () => {
			if (generative) {
				if (inputElement) {
					inputElement.removeEventListener('selectionchange', getSelectionCoordinates);
				}
			}
		};
	}, [ref]);

	const checkIfSelectionExists = () => {
		const selection = window.getSelection();

		// Checks if the current selection exists within the ref to make sure we're only showing the floating button inside of Generative
		// so we're not showing it in an unrelated TextAreaField within the same form
		if (selection && !isEmpty(selection.toString()) && selection.anchorNode.firstChild && selection.anchorNode.firstChild?.id == field.name) {
			return true;
		} else {
			return false;
		}
	};

	const getSelectionCoordinates = () => {
		const inputEl = ref.current;
		const selectionEnd = inputEl.selectionEnd;
		const caret = getCaretCoordinates(inputEl, selectionEnd);
		const { top, left } = inputEl.getBoundingClientRect();

		if (!checkIfSelectionExists()) {
			setPosition({});

			return;
		}

		setPosition({
			top: caret.top + top,
			left: caret.left + left + 8
		});
	};

	const handleChange = event => {
		if (trim) {
			helper.setValue(event.target.value.trim());
		} else {
			helper.setValue(event.target.value);
		}
	};

	const handleBlur = event => {
		if (onBlur) {
			onBlur(event, field);
		}

		field.onBlur(event);
	};

	return (
		<>
			<TextField
				{...rest}
				fullWidth
				multiline
				inputRef={ref}
				minRows={minRows}
				inputProps={{ maxLength: maxLength }}
				margin={margin ?? 'normal'}
				value={field.value ?? ''}
				error={meta.error ? true : false}
				onBlur={handleBlur}
				onChange={handleChange}
				required={required}
				helperText={<HelperText meta={meta} required={required} maxLength={maxLength} helperText={helperText} value={field.value} />}
				sx={{
					'& .MuiInputBase-root': { p: '13px' },
					'& .MuiOutlinedInput-notchedOutline': {
						...(predicted && predicted == field.value ? { borderColor: 'common.warning' } : {})
					}
				}}
			/>
			{generative && <Generative field={field} setValue={helper.setValue} position={position} onClose={() => setPosition({})} />}
		</>
	);
}
